import type { BondOrder } from '../domain/editor';
import {
  placeBondStereoLabels,
  placeChargeLabels,
  placeNodeNumbers,
  placeStereoLabels
} from '../chemistry/chargePlacement';
import { useCallback, useMemo } from 'react';
import type { DrawingLine } from '../model';
import type { DrawingAtom } from '@peruselab/structure-editor/domain/graphDrawing';
import type { BondStereoValue } from '@peruselab/structure-editor/domain/documentSelectors';
import {
  DEFAULT_BOND_DRAWING_CONFIG,
  boundedBondStrokeWidthPx,
  normalizedToPixel,
  typicalBondLengthPx,
  type PixelPoint
} from '@peruselab/structure-editor/domain/bonds';
import { roundToOneDecimal, type PaperSize } from '@peruselab/structure-editor/domain/geometry';
import {
  terminalDoubleBondCenters,
  createRingAwareBondSegments,
  createBondSegments,
  bondLineStrokeScale,
  evenParallelSegments,
  type BondSegment
} from '@peruselab/structure-editor/rendering/chemicalGeometry';
import {
  elementSymbol,
  formalChargeLabel,
  formalChargeLabelOffsetX,
  isotopeSuperscript,
  radicalLabel,
  resonanceChargeLabel
} from '@peruselab/structure-editor/chemistry/elements';
import { atomShowsLabel } from '@peruselab/structure-editor/chemistry/labelMetrics';
import {
  LABEL_BOND_GAP_PX,
  LABEL_FONT_SIZE_PX,
  atomLabelHulls,
  clipToLabelOutline,
  implicitHydrogenPosition,
  type LabelHull
} from '@peruselab/structure-editor/chemistry/labelOutline';
import { arrowheadMarkerSpec } from '@peruselab/structure-editor/rendering/arrowheadMarkers';
import type { IonicPair } from '@peruselab/structure-editor/domain/ionicPairs';
import type { SvgIonicLink } from './drawingPaperRenderModel';

const DATIVE_HEAD_LENGTH = arrowheadMarkerSpec('arrowhead-full').length;

// #391 - where a dative bond's shaft must stop: the `arrowhead-full` marker (refX at its base)
// draws the head forward from there, so the shaft ends one head-length short of the point the
// arrow should touch (the label gap short of the acceptor's letters, or pulled back off a bare
// vertex).
function dativeShaftEnd(
  from: PixelPoint,
  acceptorCentre: PixelPoint,
  labelScale: number,
  acceptorHulls: readonly LabelHull[] | undefined
): PixelPoint {
  const length = Math.hypot(acceptorCentre.x - from.x, acceptorCentre.y - from.y) || 1;
  const ux = (acceptorCentre.x - from.x) / length;
  const uy = (acceptorCentre.y - from.y) / length;
  const headLength = DATIVE_HEAD_LENGTH * labelScale;
  const tip = acceptorHulls
    ? clipToLabelOutline(acceptorCentre, from, acceptorHulls, LABEL_BOND_GAP_PX * labelScale)
    : acceptorCentre;
  const tipGap = acceptorHulls ? 0 : headLength * 0.9;
  return { x: tip.x - ux * (tipGap + headLength), y: tip.y - uy * (tipGap + headLength) };
}

const NO_IONIC_PAIRS: readonly IonicPair[] = [];

export interface UsePaperGeometryOptions {
  // ADR 0022 (as amended) -- the salt's cation-anion pairs, drawn as bolts between the ions.
  readonly ionicPairs?: readonly IonicPair[];
  readonly lines: readonly DrawingLine[];
  readonly atoms: readonly DrawingAtom[];
  readonly renderedLines: readonly DrawingLine[];
  readonly renderedAtoms: readonly DrawingAtom[];
  readonly bondOrders: Readonly<Record<string, BondOrder>>;
  readonly aromaticBondIds: ReadonlySet<string>;
  readonly aromaticRingCenters: Readonly<Record<string, { x: number; y: number }>>;
  readonly stereoBonds: Readonly<Record<string, BondStereoValue>>;
  readonly hydrogenPreviewDirections: Readonly<Record<string, Readonly<{ x: number; y: number }>>>;
  readonly paperSize: PaperSize;
  readonly viewportZoom: number;
  readonly showNodeNumbers?: boolean;
  /** #1047 -- "R" / "S" by atom id, drawn beside each stereocentre; absent when the overlay is off. */
  readonly stereoLabels?:
    | Readonly<Record<string, string>>
    | undefined; /** #780 -- "E" / "Z" by bond id, drawn beside each defined double bond; absent when the overlay is off. */
  readonly bondStereoLabels?: Readonly<Record<string, string>> | undefined;
}

/** #780 -- a double bond's E/Z label, where it is drawn. */
export interface SvgBondStereoLabel {
  readonly id: string;
  readonly text: string;
  readonly x: number;
  readonly y: number;
}

const NO_BOND_STEREO_LABELS: readonly SvgBondStereoLabel[] = [];

/**
 * The drawing paper's committed-geometry rendering projections for DrawingPaper.tsx (item 33,
 * step 4/4: see docs/repo-topology-phase-5-drawing-paper-decomposition.md). These depend on
 * `renderedLines`/`renderedAtoms` (the atom-drag-resolved committed geometry, computed by the
 * caller from the pointer hook's `atomDrag`) and `hydrogenPreviewDirections`, not on the rest of
 * pointer lifecycle's transient state -- unlike the ring/chain/shape/curved-arrow preview memos
 * and the raw draft/proposal-marker memos, which only exist during an in-progress gesture and stay
 * in the component per the characterization's Coupling 2.
 */
export function usePaperGeometry({
  lines,
  atoms,
  renderedLines,
  renderedAtoms,
  bondOrders,
  aromaticBondIds,
  aromaticRingCenters,
  stereoBonds,
  hydrogenPreviewDirections,
  paperSize,
  viewportZoom,
  showNodeNumbers = false,
  stereoLabels,
  bondStereoLabels,
  ionicPairs = NO_IONIC_PAIRS
}: UsePaperGeometryOptions) {
  // Large molecules get their whole 2D layout scaled down to fit the fixed paper (see
  // fittedCoordinates), which shrinks bond length arbitrarily. Labels, wedges, and bond stroke
  // width are otherwise a fixed reference size, so without this they overlap into an illegible
  // mess once bonds get small enough. Scaling those sizes down together with the bonds keeps the
  // depiction proportionally readable, down to a floor so it never shrinks to invisible. This is
  // a pre-zoom factor: viewport zoom then grows or shrinks labels along with everything else
  // (#385), it is not cancelled out.
  const labelScale = useMemo(() => {
    const typicalLength = typicalBondLengthPx(renderedLines, paperSize);
    return typicalLength > 0 ? Math.min(1, typicalLength / DEFAULT_BOND_DRAWING_CONFIG.lengthPx) : 1;
  }, [renderedLines, paperSize]);
  // Bond stroke width follows a step function of the majority bond's *currently rendered* length
  // (see boundedBondStrokeWidthPx). Labels and wedges scale continuously with zoom via the SVG
  // viewBox; stroke width instead steps, so it recomputes here with viewport.zoom factored in
  // rather than just the pre-zoom fitted length.
  const bondStrokeWidth = useMemo(
    () => boundedBondStrokeWidthPx(typicalBondLengthPx(renderedLines, paperSize) * viewportZoom),
    [renderedLines, paperSize, viewportZoom]
  );

  // #965 - the letter shapes of every atom that shows a symbol, in committed pixel space, placed
  // as the label group draws them (17px scaled by labelScale, its H offset scaled likewise).
  // Bonds, wedges and dative heads stop a fixed gap short of these, so nothing has to be painted
  // over a bond end and the paper's own colour shows around every letter.
  const labelOutlines = useMemo(
    () =>
      renderedAtoms.flatMap((atom) => {
        if (!atomShowsLabel(atom)) return [];
        const x = atom.position.x * paperSize.width;
        const y = atom.position.y * paperSize.height;
        const direction = hydrogenPreviewDirections[atom.id] ?? atom.implicitHydrogenDirection;
        const fontSize = LABEL_FONT_SIZE_PX * labelScale;
        const hydrogen = direction ? implicitHydrogenPosition(atom, x, y, direction, fontSize) : undefined;
        return [{ x, y, hulls: atomLabelHulls(atom, x, y, hydrogen, fontSize) }];
      }),
    [renderedAtoms, hydrogenPreviewDirections, paperSize, labelScale]
  );
  const hullsAt = useCallback(
    (point: PixelPoint) =>
      labelOutlines.find(({ x, y }) => Math.abs(x - point.x) < 0.5 && Math.abs(y - point.y) < 0.5)?.hulls,
    [labelOutlines]
  );
  // The gap a bond line's axis keeps from a letter: the visible gap plus half the line's width
  // (bond strokes are non-scaling, so their width is in screen pixels).
  const gap = LABEL_BOND_GAP_PX * labelScale;
  const bondClearance = gap + bondStrokeWidth / 2 / viewportZoom;

  // Pull each bond-axis endpoint that lands on a labelled atom back to where the centre-to-centre
  // line clears that atom's letters; leave plain-vertex (unlabelled) ends at the centre. Bails out
  // to the untrimmed axis if the two labels overlap past each other on a very short bond.
  // `endClearance` is wider for a wedge's broad end.
  const trimBondAxisToLabels = useCallback(
    (start: PixelPoint, end: PixelPoint, endClearance = bondClearance): readonly [PixelPoint, PixelPoint] => {
      const startHulls = hullsAt(start);
      const endHulls = hullsAt(end);
      const trimmedStart = startHulls ? clipToLabelOutline(start, end, startHulls, bondClearance) : start;
      const trimmedEnd = endHulls ? clipToLabelOutline(end, start, endHulls, endClearance) : end;
      const axisX = end.x - start.x;
      const axisY = end.y - start.y;
      if ((trimmedEnd.x - trimmedStart.x) * axisX + (trimmedEnd.y - trimmedStart.y) * axisY <= 0) {
        return [start, end];
      }
      return [trimmedStart, trimmedEnd];
    },
    [bondClearance, hullsAt]
  );

  // #965: the parallel lines of a double or triple bond sit off the axis, so each is trimmed
  // against the letters on its own as well -- an offset line can otherwise reach a letter's
  // corner that the axis clears.
  const trimSegmentToLabels = useCallback(
    (segment: BondSegment, start: PixelPoint, end: PixelPoint): BondSegment => {
      const startHulls = hullsAt(start);
      const endHulls = hullsAt(end);
      return {
        ...segment,
        start: startHulls ? clipToLabelOutline(segment.start, segment.end, startHulls, bondClearance) : segment.start,
        end: endHulls ? clipToLabelOutline(segment.end, segment.start, endHulls, bondClearance) : segment.end
      };
    },
    [bondClearance, hullsAt]
  );

  // The faint WebGL under-layer draws every bond as one line; with no box over a label any more it
  // must stop at the letters too.
  const trimmedLines = useMemo(
    () =>
      renderedLines.map((line) => {
        const [start, end] = trimBondAxisToLabels(
          normalizedToPixel(line.start, paperSize),
          normalizedToPixel(line.end, paperSize)
        );
        return {
          ...line,
          start: { x: start.x / paperSize.width, y: start.y / paperSize.height },
          end: { x: end.x / paperSize.width, y: end.y / paperSize.height }
        };
      }),
    [renderedLines, paperSize, trimBondAxisToLabels]
  );

  // #273 B4 / #391: for each dative bond, which end is the acceptor and where its shaft must stop
  // so the `arrowhead-full` <marker> draws the head from there. Computed before svgLines.
  const dativeShaftEnds = useMemo(
    () =>
      renderedLines.flatMap((line) => {
        const stereo = stereoBonds[line.id];
        if (stereo?.type !== 'dative') return [];
        const originalLine = lines.find((candidate) => candidate.id === line.id) ?? line;
        const acceptorAtStart = atoms.some(
          (atom) =>
            atom.id === stereo.acceptorNodeId &&
            Math.abs(atom.position.x - originalLine.start.x) <= 1e-6 &&
            Math.abs(atom.position.y - originalLine.start.y) <= 1e-6
        );
        const from = normalizedToPixel(acceptorAtStart ? line.end : line.start, paperSize);
        const acceptorCentre = normalizedToPixel(acceptorAtStart ? line.start : line.end, paperSize);
        return [
          {
            id: line.id,
            acceptorAtStart,
            end: dativeShaftEnd(from, acceptorCentre, labelScale, hullsAt(acceptorCentre))
          }
        ];
      }),
    [atoms, hullsAt, labelScale, lines, renderedLines, stereoBonds, paperSize]
  );

  const terminalCenters = useMemo(
    () =>
      terminalDoubleBondCenters(
        renderedLines.map((line) => ({
          id: line.id,
          order: bondOrders[line.id] ?? 1,
          start: normalizedToPixel(line.start, paperSize),
          end: normalizedToPixel(line.end, paperSize)
        }))
      ),
    [renderedLines, bondOrders, paperSize]
  );

  const svgLines = useMemo(
    () =>
      renderedLines.flatMap((line) => {
        if (stereoBonds[line.id]?.type === 'wedge') return [];
        const centres = [normalizedToPixel(line.start, paperSize), normalizedToPixel(line.end, paperSize)] as const;
        let [start, end] = trimBondAxisToLabels(...centres);
        // #391: draw a dative bond donor -> (shaft end), oriented so `marker-end` lands the
        // arrowhead on the acceptor; the shaft stops one head-length short so the marker forms
        // the tip.
        const dative = dativeShaftEnds.find((candidate) => candidate.id === line.id);
        if (dative) {
          start = dative.acceptorAtStart ? end : start;
          end = dative.end;
        }
        // #422: any double bond in exactly one ring (not just aromatic ones) gets its second line
        // inset toward the ring interior -- a crossed-double explicitly wants the symmetric "X"
        // look instead, so it keeps the plain fallback even when it happens to be in a ring.
        const ringCenter = aromaticRingCenters[line.id];
        const sideCenter = ringCenter ? normalizedToPixel(ringCenter, paperSize) : terminalCenters[line.id];
        const crossed = stereoBonds[line.id]?.type === 'crossed-double';
        const stereoType = stereoBonds[line.id]?.type;
        const kind =
          stereoType === 'hydrogen' || stereoType === 'wavy' || stereoType === 'agostic' ? stereoType : undefined;
        const segments =
          !crossed && sideCenter
            ? createRingAwareBondSegments(
                ringCenter ? start : centres[0],
                ringCenter ? end : centres[1],
                bondOrders[line.id] ?? 1,
                sideCenter,
                aromaticBondIds.has(line.id),
                ringCenter !== undefined || terminalCenters[line.id]!.insetStart,
                ringCenter !== undefined || terminalCenters[line.id]!.insetEnd
              )
            : // A symmetric double/triple bond is laid out from the atom centres, each line then
              // trimmed on its own, so every line keeps the same gap from the letters.
              (bondOrders[line.id] ?? 1) > 1
              ? createBondSegments(...centres, bondOrders[line.id] ?? 1, crossed)
              : createBondSegments(start, end, bondOrders[line.id] ?? 1, crossed);
        const eachTrimmed =
          segments.length > 1 ? segments.map((segment) => trimSegmentToLabels(segment, ...centres)) : segments;
        // #1130: four to six lines end square, all the same length.
        const trimmed = segments.length > 3 ? evenParallelSegments(segments, eachTrimmed) : eachTrimmed;
        return trimmed.map((segment, index) => ({
          id: `${line.id}-${index}`,
          bondId: line.id,
          x1: roundToOneDecimal(segment.start.x),
          y1: roundToOneDecimal(segment.start.y),
          x2: roundToOneDecimal(segment.end.x),
          y2: roundToOneDecimal(segment.end.y),
          aromatic: aromaticBondIds.has(line.id),
          aromaticInner: segment.aromaticInner === true,
          // dative is order 1 -> one segment; put the head on the acceptor end of it.
          dativeHead: dative !== undefined,
          ...(kind ? { kind, markSize: 3 * labelScale } : {}),
          ...(bondLineStrokeScale(bondOrders[line.id] ?? 1) !== 1
            ? { strokeScale: bondLineStrokeScale(bondOrders[line.id] ?? 1) }
            : {})
        }));
      }),
    [
      aromaticBondIds,
      aromaticRingCenters,
      terminalCenters,
      bondOrders,
      dativeShaftEnds,
      labelScale,
      renderedLines,
      stereoBonds,
      paperSize,
      trimBondAxisToLabels,
      trimSegmentToLabels
    ]
  );

  const stereoWedges = useMemo(
    () =>
      renderedLines.flatMap((line) => {
        const stereo = stereoBonds[line.id];
        if (stereo?.type !== 'wedge') return [];
        const originalLine = lines.find((candidate) => candidate.id === line.id) ?? line;
        const sourceIsStart = atoms.some(
          (atom) =>
            atom.id === stereo.fromNodeId &&
            Math.abs(atom.position.x - originalLine.start.x) <= 1e-6 &&
            Math.abs(atom.position.y - originalLine.start.y) <= 1e-6
        );
        // Deliberately not counter-scaled against viewport.zoom (unlike atom labels below): a wedge
        // is part of the bond geometry and should grow with it on zoom, the same way its length
        // already does, rather than staying pixel-locked to the screen regardless of zoom level.
        const halfWidth = Math.max(1.5, 5 * labelScale);
        // #387/#965: like a plain bond, a wedge stops short of the letters - its narrow point at
        // the source symbol, its broad end (whose corners reach halfWidth off the axis) at the far one.
        const [from, to] = trimBondAxisToLabels(
          normalizedToPixel(sourceIsStart ? line.start : line.end, paperSize),
          normalizedToPixel(sourceIsStart ? line.end : line.start, paperSize),
          gap + halfWidth
        );
        const length = Math.hypot(to.x - from.x, to.y - from.y) || 1;
        const nx = -(to.y - from.y) / length;
        const ny = (to.x - from.x) / length;
        return [{ id: line.id, style: stereo.style, from, to, nx, ny, halfWidth }];
      }),
    [atoms, gap, labelScale, lines, renderedLines, stereoBonds, paperSize, trimBondAxisToLabels]
  );

  const { atoms: svgAtoms, bondStereo: svgBondStereoLabels } = useMemo(() => {
    const projected = renderedAtoms.map((atom) => {
      const x = roundToOneDecimal(atom.position.x * paperSize.width);
      const y = roundToOneDecimal(atom.position.y * paperSize.height);
      const hydrogenDirection = hydrogenPreviewDirections[atom.id] ?? atom.implicitHydrogenDirection;
      // #977: in the label group's own (pre-labelScale) units, at the unscaled font size.
      const hydrogen = hydrogenDirection
        ? implicitHydrogenPosition(atom, x, y, hydrogenDirection, LABEL_FONT_SIZE_PX)
        : undefined;
      const hydrogenX = hydrogen ? roundToOneDecimal(hydrogen.x) : undefined;
      const hydrogenY = hydrogen ? roundToOneDecimal(hydrogen.y) : undefined;
      return {
        ...atom,
        x,
        y,
        hydrogenX,
        hydrogenY,
        symbol: atom.labelOverride ?? isotopeSuperscript(atom.isotope) + elementSymbol(atom.atomicNumber),
        chargeLabel:
          atom.resonanceCharge !== undefined
            ? resonanceChargeLabel(atom.resonanceCharge)
            : formalChargeLabel(atom.formalCharge),
        chargeOffsetX: formalChargeLabelOffsetX(atom.atomicNumber),
        radicalLabel: radicalLabel(atom.radicalElectronCount)
      };
    });
    const labelAtoms = projected.map((atom) => ({
      id: atom.id,
      x: atom.x,
      y: atom.y,
      text: atom.chargeLabel,
      radical: atom.radicalLabel,
      offsetX: atom.chargeOffsetX,
      hulls: atomShowsLabel(atom)
        ? atomLabelHulls(
            atom,
            atom.x,
            atom.y,
            atom.hydrogenX !== undefined && atom.hydrogenY !== undefined
              ? {
                  x: atom.x + (atom.hydrogenX - atom.x) * labelScale,
                  y: atom.y + (atom.hydrogenY - atom.y) * labelScale
                }
              : undefined,
            LABEL_FONT_SIZE_PX * labelScale
          )
        : []
    }));
    const labelBonds = renderedLines.map((line) => ({
      from: normalizedToPixel(line.start, paperSize),
      to: normalizedToPixel(line.end, paperSize),
      radius:
        stereoBonds[line.id]?.type === 'wedge'
          ? 5 * labelScale
          : (bondOrders[line.id] ?? 1) > 1
            ? 4 * labelScale
            : bondStrokeWidth / viewportZoom / 2
    }));
    const placements = placeChargeLabels(labelAtoms, labelBonds, labelScale);
    const numbers = showNodeNumbers
      ? placeNodeNumbers(
          labelAtoms,
          labelBonds,
          labelScale,
          [...placements.values()].map((p) => p.box)
        )
      : undefined;
    const stereo = stereoLabels
      ? placeStereoLabels(
          labelAtoms,
          labelBonds,
          labelScale,
          [...placements.values(), ...(numbers?.values() ?? [])].map((p) => p.box),
          stereoLabels
        )
      : undefined;
    // #780 -- E/Z beside each defined double bond, on the side its substituents leave free, clear of
    // every label already placed and of the bonds.
    const bondEntries = bondStereoLabels
      ? renderedLines.flatMap((line, index) => {
          const label = bondStereoLabels[line.id];
          const bond = labelBonds[index]!;
          return label ? [{ id: line.id, from: bond.from, to: bond.to, text: `(${label})` }] : [];
        })
      : [];
    const bondPlacements = bondEntries.length
      ? placeBondStereoLabels(
          bondEntries,
          labelBonds,
          labelScale,
          [...placements.values(), ...(numbers?.values() ?? []), ...(stereo?.values() ?? [])].map((p) => p.box)
        )
      : undefined;
    const bondStereo: readonly SvgBondStereoLabel[] = bondEntries.flatMap((entry) => {
      const placed = bondPlacements?.get(entry.id);
      return placed ? [{ id: entry.id, text: entry.text, x: placed.x, y: placed.y }] : [];
    });
    const atoms = projected.map((atom, index) => {
      const number = numbers?.get(atom.id);
      const charge = placements.get(atom.id);
      const stereoLabel = stereo?.get(atom.id);
      return {
        ...atom,
        ...(stereoLabel
          ? { stereoLabel: `(${stereoLabels![atom.id]})`, stereoLabelX: stereoLabel.x, stereoLabelY: stereoLabel.y }
          : {}),
        nodeNumber: index + 1,
        nodeNumberX: number?.x ?? atom.x + 8 * labelScale,
        nodeNumberY: number?.y ?? atom.y - 8 * labelScale,
        chargeX: charge ? atom.x + (charge.x - atom.x) / labelScale : atom.x + atom.chargeOffsetX,
        chargeY: charge ? atom.y + (charge.y - atom.y) / labelScale : atom.y - 9
      };
    });
    return { atoms, bondStereo: bondStereo.length ? bondStereo : NO_BOND_STEREO_LABELS };
  }, [
    hydrogenPreviewDirections,
    renderedAtoms,
    paperSize,
    renderedLines,
    stereoBonds,
    bondOrders,
    labelScale,
    bondStrokeWidth,
    viewportZoom,
    showNodeNumbers,
    stereoLabels,
    bondStereoLabels
  ]);

  // ADR 0022 (as amended) -- each bolt runs between its two ions' live positions, so it stretches
  // as an ion is dragged, and stops short of their letters like any bond.
  const ionicLinks = useMemo(
    () =>
      ionicPairs.flatMap((pair): SvgIonicLink[] => {
        const cation = renderedAtoms.find((atom) => atom.id === pair.cationId);
        const anion = renderedAtoms.find((atom) => atom.id === pair.anionId);
        if (!cation || !anion) return [];
        const [from, to] = trimBondAxisToLabels(
          normalizedToPixel(cation.position, paperSize),
          normalizedToPixel(anion.position, paperSize)
        );
        return [
          {
            ...pair,
            x1: roundToOneDecimal(from.x),
            y1: roundToOneDecimal(from.y),
            x2: roundToOneDecimal(to.x),
            y2: roundToOneDecimal(to.y),
            width: 5 * labelScale
          }
        ];
      }),
    [ionicPairs, labelScale, paperSize, renderedAtoms, trimBondAxisToLabels]
  );

  return {
    labelScale,
    bondStrokeWidth,
    svgLines,
    stereoWedges,
    svgAtoms,
    svgBondStereoLabels,
    trimmedLines,
    ionicLinks
  };
}
